/**
 * Stable per-object layer ids (`__uid`).
 *
 * Before this module, only a few insert paths assigned `__uid` and
 * LayersPanel backfilled the rest using the array index, so ids changed
 * across sessions and reorders. Ids assigned here are random and persist via
 * LAYER_CUSTOM_PROPS, so an object keeps its id once it has one.
 */

import type { fabric } from 'fabric';

type UidObject = fabric.Object & { __uid?: string };

/** Mint a fresh layer id. */
export function createObjectUid(): string {
  return `layer-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

/** Read an object's layer id, or null when it has none yet. */
export function uidOf(obj: fabric.Object | null | undefined): string | null {
  return (obj as UidObject | null | undefined)?.__uid || null;
}

/** Assign a layer id if missing. Returns the (possibly existing) id. */
export function ensureObjectUid(obj: fabric.Object): string {
  const o = obj as UidObject;
  if (!o.__uid) o.__uid = createObjectUid();
  return o.__uid;
}

/** Assign layer ids to every top-level object that lacks one. */
export function ensureCanvasUids(canvas: fabric.Canvas | fabric.StaticCanvas): void {
  canvas.getObjects().forEach((obj) => {
    ensureObjectUid(obj);
  });
}
